/**
 * DialogueSystem — typewriter-style NPC dialogue overlay for Phaser scenes.
 *
 * Features:
 *  - Character-by-character typewriter reveal at a cozy pace (~28 chars/sec)
 *  - Per-glyph audio click via inline Web Audio API (very subtle)
 *  - NPC portrait placeholder (coloured rectangle, hashed from NPC name)
 *  - Choice buttons for branching conversations (separate box above dialogue)
 *  - Skip / fast-forward on advance(): first call shows full text, second advances
 *  - Data-driven: accept DialogueData directly, lines[], or load from JSON by id
 *  - Zod-validated JSON format (see dialogue.schema.ts)
 *
 * Usage:
 *   const dlg = new DialogueSystem(this);    // 'this' is a Phaser.Scene
 *   dlg.showLines('Guard', ['Halt!', 'Move along.']);
 *   dlg.show(myDialogueData);
 *   await dlg.showById('innkeeper_greeting'); // loads /dialogue/innkeeper_greeting.json
 *
 *   // In update loop:
 *   if (JustDown(eKey)) dlg.advance();
 *   if (dlg.isActive()) return; // block movement
 *
 *   // On scene shutdown:
 *   dlg.destroy();
 */

import * as Phaser from 'phaser';
import type { DialogueData, DialogueNode, DialogueChoice } from './dialogue.schema';
import { DialogueDataSchema } from './dialogue.schema';
import {
  C5_BAND,
  C5_BAND_LINE,
  C5_HANKO,
  C5_INK,
  C5_INK_DIM,
  C5_PAPER,
  C5_PAPER_DK,
  C5_PAPER_HI,
  hexToCSS,
} from './palette';
import { drawPaperSlip, lerpColor } from './c5/paperMaterial';
import { FontManager } from './FontManager';

// ── Layout ────────────────────────────────────────────────────────────────────

const DIALOGUE_DEPTH = 500;

/** Main dialogue box — fixed height at bottom of screen. */
const BOX_H = 110;
const BOX_MAX_W = 640;
const BOX_MARGIN_BOTTOM = 14;
const BOX_PADDING = 12;

/** NPC portrait area inside the left side of the dialogue box. */
const PORTRAIT_W = 64;
const PORTRAIT_H = 86;

/** Typewriter character interval in ms. 36 ms ≈ 27–28 chars/sec (cozy). */
const GLYPH_INTERVAL_MS = 36;

/** Play glyph sound only on every Nth non-space character (avoid spam). */
const GLYPH_SOUND_EVERY = 2;

/** Per-choice button height and vertical gap in the choice box. */
const BTN_H = 22;
const BTN_GAP = 4;
const CHOICE_BOX_PADDING = 10;
const CHOICE_BOX_GAP = 6; // gap between choice box bottom and dialogue box top

// ── Colors — C5 "Indigo & Ink" paper slip (docs/design/ui-c5-indigo-ink.md) ───

/** The dialogue slip itself is drawn as paper by `drawPaperSlip`; the
 *  Rectangle behind it survives only as the click target for advance(). */
const BOX_ALPHA = 0.97;
const NAME_CSS = hexToCSS(C5_HANKO);      // speaker name reads as a hanko label
const BODY_CSS = hexToCSS(C5_INK);        // brush ink on paper
const HINT_CSS = hexToCSS(C5_INK_DIM);    // secondary ink
const PORTRAIT_MAT = C5_PAPER_DK;         // paper mat around the portrait
const PORTRAIT_WASH = 0.55;               // blend of the hashed hue toward paper
const CHOICE_BG = C5_PAPER_HI;            // choice slips sit lighter than the sheet
const CHOICE_HOVER = C5_PAPER;            // ink-warmed on hover
const CHOICE_TEXT_CSS = hexToCSS(C5_INK);
const CHOICE_BOX_BG = C5_BAND;            // choices rest on a quiet console tray
const CHOICE_BOX_ALPHA = 0.72;
const CHOICE_SLIP_FADE_MS = 180;

/** Portrait placeholder colours — picked by hashing NPC name. */
const PORTRAIT_PALETTE = [0xa8614a, 0x3a7a5a, 0x3a5a9a, 0x6a3a8a, 0xd46a2a];

function portraitColor(npcName: string): number {
  let h = 0;
  for (let i = 0; i < npcName.length; i++) {
    h = (Math.imul(h, 31) + npcName.charCodeAt(i)) | 0;
  }
  return PORTRAIT_PALETTE[Math.abs(h) % PORTRAIT_PALETTE.length];
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface ChoiceButton {
  bg: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
}

// ── DialogueSystem ────────────────────────────────────────────────────────────

export class DialogueSystem {
  private readonly scene: Phaser.Scene;

  // ── Static UI objects (created once, reused across dialogues) ──────────────
  /** Paper material for the dialogue slip. Drawn once at buildUI time. */
  private paperGfx: Phaser.GameObjects.Graphics | null = null;
  private box: Phaser.GameObjects.Rectangle | null = null;
  private portraitBg: Phaser.GameObjects.Rectangle | null = null;
  private portraitInitial: Phaser.GameObjects.Text | null = null;
  private nameText: Phaser.GameObjects.Text | null = null;
  private bodyText: Phaser.GameObjects.Text | null = null;
  private hintText: Phaser.GameObjects.Text | null = null;

  // ── Dynamic choice UI (recreated per node) ─────────────────────────────────
  private choiceBoxBg: Phaser.GameObjects.Rectangle | null = null;
  private choiceGfx: Phaser.GameObjects.Graphics | null = null;
  private choiceButtons: ChoiceButton[] = [];

  // ── Dialogue state ─────────────────────────────────────────────────────────
  private data: DialogueData | null = null;
  private nodeMap = new Map<string, DialogueNode>();
  private currentNodeId: string | null = null;
  private fullText = '';
  private displayedChars = 0;
  private typewriterTimer: Phaser.Time.TimerEvent | null = null;
  private glyphSoundCounter = 0;

  // ── Audio ──────────────────────────────────────────────────────────────────
  private audioCtx: AudioContext | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /** Returns true while a dialogue is being displayed. */
  isActive(): boolean {
    return this.data !== null;
  }

  /**
   * Display a simple sequence of lines (backward-compatible path).
   * Converts the lines array into an inline DialogueData with no choices.
   */
  showLines(npcName: string, lines: string[], portrait?: string): void {
    if (lines.length === 0) return;
    const nodes: DialogueNode[] = lines.map((text, i) => ({
      id: String(i),
      text,
      next: i < lines.length - 1 ? String(i + 1) : 'end',
    }));
    this.show({ npcName, portrait, nodes });
  }

  /**
   * Fetch and display a JSON dialogue file from `/dialogue/{id}.json`.
   * The file is Zod-validated before display; errors are logged and ignored.
   */
  async showById(dialogueId: string): Promise<void> {
    try {
      const resp = await fetch(`/dialogue/${dialogueId}.json`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const raw: unknown = await resp.json();
      const result = DialogueDataSchema.safeParse(raw);
      if (!result.success) {
        console.error('[DialogueSystem] Invalid dialogue JSON for', dialogueId, result.error);
        return;
      }
      this.show(result.data);
    } catch (e) {
      console.error('[DialogueSystem] Failed to load dialogue', dialogueId, e);
    }
  }

  /** Display a pre-validated DialogueData object. */
  show(data: DialogueData): void {
    this.data = data;
    this.nodeMap.clear();
    for (const node of data.nodes) this.nodeMap.set(node.id, node);

    this.initAudio();
    this.buildUI();
    this.updatePortrait(data.npcName);
    this.nameText!.setText(data.npcName);
    this.setVisible(true);
    this.goToNode(data.nodes[0].id);
  }

  /**
   * Handle player advance input (keyboard E / click on dialogue box).
   *
   * - While typewriter is running: skip to full text.
   * - If choices are shown: ignored — player must click a choice.
   * - Otherwise: advance to next node or close.
   */
  advance(): void {
    if (!this.data || !this.currentNodeId) return;

    // If typewriter is still printing, skip to full text first.
    if (this.displayedChars < this.fullText.length) {
      this.finishTypewriter();
      return;
    }

    const node = this.nodeMap.get(this.currentNodeId);
    if (!node) return;

    // Choices require a click — keyboard advance is intentionally blocked.
    if (node.choices && node.choices.length > 0) return;

    this.advanceFromNode(node);
  }

  /**
   * Destroy all owned game objects and free audio resources.
   * Call this in the scene's shutdown() or destroy() handler.
   */
  destroy(): void {
    this.stopTypewriter();
    this.clearChoiceUI();

    const staticObjs = [
      this.paperGfx,
      this.box,
      this.portraitBg,
      this.portraitInitial,
      this.nameText,
      this.bodyText,
      this.hintText,
    ];
    for (const obj of staticObjs) obj?.destroy();
    this.paperGfx = null;
    this.box = null;
    this.portraitBg = null;
    this.portraitInitial = null;
    this.nameText = null;
    this.bodyText = null;
    this.hintText = null;

    this.data = null;
    this.nodeMap.clear();
    this.currentNodeId = null;

    if (this.audioCtx) {
      this.audioCtx.close().catch(() => undefined);
      this.audioCtx = null;
    }
  }

  // ── UI construction ────────────────────────────────────────────────────────

  /** Build the static dialogue box elements once; skip if already built. */
  private buildUI(): void {
    if (this.box) return;

    const { width: sw, height: sh } = this.scene.cameras.main;
    const boxW = Math.min(sw - 40, BOX_MAX_W);
    const boxX = sw / 2;
    const boxY = sh - BOX_H / 2 - BOX_MARGIN_BOTTOM;

    // ── Paper slip (the material) ─────────────────────────────────────────────
    // The traveler is being spoken to; the words arrive on paper. The slip is
    // drawn once here — layout is fixed, so there is nothing to redraw per node.
    this.paperGfx = this.scene.add.graphics();
    drawPaperSlip(this.paperGfx, boxX - boxW / 2, boxY - BOX_H / 2, boxW, BOX_H, {
      radius: 2,
      alpha: BOX_ALPHA,
      shadow: 4,
    });
    this.paperGfx.setScrollFactor(0);
    this.paperGfx.setDepth(DIALOGUE_DEPTH - 1);
    (this.scene as any).addToUI?.(this.paperGfx);

    // ── Click target ──────────────────────────────────────────────────────────
    // Invisible: the paper above is the visible surface. Kept as a Rectangle so
    // the advance()-on-click behaviour and the visibility/destroy paths are
    // exactly what they were before the restyle.
    this.box = this.scene.add.rectangle(boxX, boxY, boxW, BOX_H, C5_PAPER, 0);
    this.box.setScrollFactor(0);
    this.box.setDepth(DIALOGUE_DEPTH);
    this.box.setInteractive();
    this.box.on('pointerdown', () => this.advance());
    (this.scene as any).addToUI?.(this.box);

    // ── Portrait placeholder ──────────────────────────────────────────────────
    const portX = boxX - boxW / 2 + BOX_PADDING + PORTRAIT_W / 2;
    const portY = boxY;
    this.portraitBg = this.scene.add.rectangle(portX, portY, PORTRAIT_W, PORTRAIT_H, 0x888888);
    this.portraitBg.setStrokeStyle(2, PORTRAIT_MAT);
    this.portraitBg.setScrollFactor(0);
    this.portraitBg.setDepth(DIALOGUE_DEPTH + 1);
    (this.scene as any).addToUI?.(this.portraitBg);

    this.portraitInitial = this.scene.add.text(portX, portY, '',
      FontManager.style('name', { fontSize: '22px', color: hexToCSS(C5_INK) }),
    );
    this.portraitInitial.setOrigin(0.5, 0.5);
    this.portraitInitial.setScrollFactor(0);
    this.portraitInitial.setDepth(DIALOGUE_DEPTH + 2);
    (this.scene as any).addToUI?.(this.portraitInitial);

    // ── Text area ─────────────────────────────────────────────────────────────
    const textLeft = boxX - boxW / 2 + PORTRAIT_W + BOX_PADDING * 2;
    const textRight = boxX + boxW / 2 - BOX_PADDING;
    const textWrapW = textRight - textLeft;

    this.nameText = this.scene.add.text(
      textLeft,
      boxY - BOX_H / 2 + BOX_PADDING,
      '',
      FontManager.style('name', { color: NAME_CSS }),
    );
    this.nameText.setScrollFactor(0);
    this.nameText.setDepth(DIALOGUE_DEPTH + 1);
    (this.scene as any).addToUI?.(this.nameText);

    this.bodyText = this.scene.add.text(
      textLeft,
      boxY - BOX_H / 2 + BOX_PADDING + 20,
      '',
      // High-contrast subtitles own the body/hint colour (white on a black
      // stroke); ink-on-paper only applies when that a11y option is off.
      FontManager.style('body', {
        wordWrap: { width: textWrapW },
        ...(FontManager.getSettings().highContrastSubtitles ? {} : { color: BODY_CSS }),
      }),
    );
    this.bodyText.setScrollFactor(0);
    this.bodyText.setDepth(DIALOGUE_DEPTH + 1);
    (this.scene as any).addToUI?.(this.bodyText);

    this.hintText = this.scene.add.text(
      textRight,
      boxY + BOX_H / 2 - 8,
      '',
      FontManager.style('hint', {
        fontStyle: 'italic',
        ...(FontManager.getSettings().highContrastSubtitles ? {} : { color: HINT_CSS }),
      }),
    );
    this.hintText.setOrigin(1, 1);
    this.hintText.setScrollFactor(0);
    this.hintText.setDepth(DIALOGUE_DEPTH + 1);
    (this.scene as any).addToUI?.(this.hintText);
  }

  private updatePortrait(npcName: string): void {
    // Wash the hashed portrait toward the paper it is mounted on, so the card
    // reads as a painted plate rather than a saturated placeholder block.
    this.portraitBg?.setFillStyle(lerpColor(portraitColor(npcName), C5_PAPER, PORTRAIT_WASH));
    this.portraitInitial?.setText(npcName[0]?.toUpperCase() ?? '');
  }

  private setVisible(visible: boolean): void {
    this.paperGfx?.setVisible(visible);
    this.box?.setVisible(visible);
    this.portraitBg?.setVisible(visible);
    this.portraitInitial?.setVisible(visible);
    this.nameText?.setVisible(visible);
    this.bodyText?.setVisible(visible);
    this.hintText?.setVisible(visible);
  }

  // ── Node traversal ─────────────────────────────────────────────────────────

  private goToNode(nodeId: string): void {
    if (nodeId === 'end' || nodeId === '') {
      this.close();
      return;
    }
    const node = this.nodeMap.get(nodeId);
    if (!node) {
      this.close();
      return;
    }
    this.currentNodeId = nodeId;
    this.clearChoiceUI();
    this.hintText?.setText('');
    this.startTypewriter(node.text);
  }

  private advanceFromNode(node: DialogueNode): void {
    if (node.next) {
      this.goToNode(node.next);
      return;
    }
    // Linear advance: find position in nodes array
    const idx = this.data!.nodes.findIndex(n => n.id === node.id);
    if (idx >= 0 && idx < this.data!.nodes.length - 1) {
      this.goToNode(this.data!.nodes[idx + 1].id);
    } else {
      this.close();
    }
  }

  private close(): void {
    this.stopTypewriter();
    this.clearChoiceUI();
    this.data = null;
    this.nodeMap.clear();
    this.currentNodeId = null;
    this.fullText = '';
    this.displayedChars = 0;
    this.setVisible(false);
  }

  // ── Typewriter ─────────────────────────────────────────────────────────────

  private startTypewriter(text: string): void {
    this.stopTypewriter();
    this.fullText = text;
    this.displayedChars = 0;
    this.glyphSoundCounter = 0;
    this.bodyText?.setText('');

    this.typewriterTimer = this.scene.time.addEvent({
      delay: GLYPH_INTERVAL_MS,
      repeat: text.length - 1,
      callback: this.onTypewriterTick,
      callbackScope: this,
    });
  }

  private onTypewriterTick(): void {
    if (this.displayedChars >= this.fullText.length) return;

    this.displayedChars++;
    const ch = this.fullText[this.displayedChars - 1];
    this.bodyText?.setText(this.fullText.slice(0, this.displayedChars));

    // Play glyph sound for non-space characters, every Nth one.
    if (ch && ch.trim() !== '') {
      this.glyphSoundCounter++;
      if (this.glyphSoundCounter % GLYPH_SOUND_EVERY === 1) {
        this.playGlyph();
      }
    }

    if (this.displayedChars >= this.fullText.length) {
      this.stopTypewriter();
      this.onTypewriterComplete();
    }
  }

  /** Skip to full text without waiting for the typewriter to finish. */
  private finishTypewriter(): void {
    this.stopTypewriter();
    this.displayedChars = this.fullText.length;
    this.bodyText?.setText(this.fullText);
    this.onTypewriterComplete();
  }

  private stopTypewriter(): void {
    if (this.typewriterTimer) {
      this.typewriterTimer.remove();
      this.typewriterTimer = null;
    }
  }

  private onTypewriterComplete(): void {
    const node = this.currentNodeId ? this.nodeMap.get(this.currentNodeId) : null;
    if (!node) return;

    if (node.choices && node.choices.length > 0) {
      this.showChoices(node.choices);
    } else {
      const isLast =
        !node.next &&
        node.id === this.data?.nodes[this.data.nodes.length - 1]?.id;
      this.hintText?.setText(isLast ? '[E] or click to close' : '[E] or click to continue ▶');
    }
  }

  // ── Choice UI ──────────────────────────────────────────────────────────────

  /**
   * Render choice buttons in a separate box positioned directly above the
   * dialogue box.  Each button is an interactive Rectangle + Text pair.
   */
  private showChoices(choices: DialogueChoice[]): void {
    this.clearChoiceUI();

    const { width: sw, height: sh } = this.scene.cameras.main;
    const boxW = Math.min(sw - 40, BOX_MAX_W);
    const boxX = sw / 2;
    const dialogueBoxTopY = sh - BOX_H - BOX_MARGIN_BOTTOM;

    const choiceBoxH =
      choices.length * BTN_H + (choices.length - 1) * BTN_GAP + CHOICE_BOX_PADDING * 2;
    const choiceBoxY = dialogueBoxTopY - CHOICE_BOX_GAP - choiceBoxH / 2;

    // Choice box background
    this.choiceBoxBg = this.scene.add.rectangle(
      boxX, choiceBoxY, boxW, choiceBoxH, CHOICE_BOX_BG, CHOICE_BOX_ALPHA,
    );
    this.choiceBoxBg.setStrokeStyle(1, C5_BAND_LINE, 0.7);
    this.choiceBoxBg.setScrollFactor(0);
    this.choiceBoxBg.setDepth(DIALOGUE_DEPTH + 2);
    (this.scene as any).addToUI?.(this.choiceBoxBg);

    // Buttons — each choice is its own smaller paper slip on the tray.
    const btnW = boxW - BOX_PADDING * 2;
    const firstBtnY = choiceBoxY - choiceBoxH / 2 + CHOICE_BOX_PADDING + BTN_H / 2;

    // One Graphics carries the paper + shadow for every slip in this node.
    this.choiceGfx = this.scene.add.graphics();
    this.choiceGfx.setScrollFactor(0);
    this.choiceGfx.setDepth(DIALOGUE_DEPTH + 2.5);
    (this.scene as any).addToUI?.(this.choiceGfx);

    choices.forEach((choice, i) => {
      const btnY = firstBtnY + i * (BTN_H + BTN_GAP);
      const btnX = boxX;

      drawPaperSlip(this.choiceGfx!, btnX - btnW / 2, btnY - BTN_H / 2, btnW, BTN_H, {
        radius: 2,
        shadow: 2,
      });

      // The Rectangle stays the hit target and the hover wash; the paper it
      // sits on is drawn above. Alpha keeps the ink warm rather than gray.
      const bg = this.scene.add.rectangle(btnX, btnY, btnW, BTN_H, CHOICE_BG, 0);
      bg.setScrollFactor(0);
      bg.setDepth(DIALOGUE_DEPTH + 3);
      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerover', () => bg.setFillStyle(CHOICE_HOVER, 0.85));
      bg.on('pointerout', () => bg.setFillStyle(CHOICE_BG, 0));
      bg.on('pointerdown', () => this.selectChoice(choice));
      (this.scene as any).addToUI?.(bg);

      const label = this.scene.add.text(btnX, btnY, choice.text,
        FontManager.style('choice', { color: CHOICE_TEXT_CSS }),
      );
      label.setOrigin(0.5, 0.5);
      label.setScrollFactor(0);
      label.setDepth(DIALOGUE_DEPTH + 4);
      (this.scene as any).addToUI?.(label);

      this.choiceButtons.push({ bg, label });
    });

    // Gentle arrival — the slips settle in rather than popping (doctrine §4).
    const arriving: Phaser.GameObjects.GameObject[] = [
      this.choiceBoxBg, this.choiceGfx,
      ...this.choiceButtons.map(b => b.label),
    ];
    for (const obj of arriving) (obj as { setAlpha?: (a: number) => void }).setAlpha?.(0);
    this.scene.tweens.add({
      targets: arriving,
      alpha: 1,
      duration: CHOICE_SLIP_FADE_MS,
      ease: 'Sine.easeOut',
    });
  }

  private selectChoice(choice: DialogueChoice): void {
    this.playGlyph(); // audio feedback on selection
    this.goToNode(choice.next);
  }

  private clearChoiceUI(): void {
    // Kill the arrival tween first — its targets are about to be destroyed.
    if (this.choiceBoxBg) this.scene.tweens.killTweensOf(this.choiceBoxBg);
    if (this.choiceGfx) this.scene.tweens.killTweensOf(this.choiceGfx);
    for (const btn of this.choiceButtons) this.scene.tweens.killTweensOf(btn.label);

    this.choiceBoxBg?.destroy();
    this.choiceBoxBg = null;
    this.choiceGfx?.destroy();
    this.choiceGfx = null;
    for (const btn of this.choiceButtons) {
      btn.bg.destroy();
      btn.label.destroy();
    }
    this.choiceButtons = [];
  }

  // ── Audio ──────────────────────────────────────────────────────────────────

  /** Lazily create AudioContext on first dialogue show (after user gesture). */
  private initAudio(): void {
    if (this.audioCtx) return;
    try {
      this.audioCtx = new AudioContext();
    } catch {
      // Web Audio not available — silently skip
    }
  }

  /**
   * Play a very soft, short sine-wave click — the per-glyph typewriter sound.
   * Frequency varies slightly to avoid a mechanical feel.
   */
  private playGlyph(): void {
    if (!this.audioCtx || this.audioCtx.state === 'suspended') return;
    try {
      const ctx = this.audioCtx;
      const now = ctx.currentTime;
      const duration = 0.038;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.value = 720 + Math.random() * 120; // 720–840 Hz, slight pitch variation

      gain.gain.setValueAtTime(0.045, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + duration);
    } catch {
      // Ignore audio errors (context may have been closed)
    }
  }
}
